/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { Checkmark24Regular as Check } from '@fluentui/react-icons';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';

export interface StepItem {
  id: string;
  title: string;
  description?: string;
  isOptional?: boolean;
}

const useStyles = makeStyles({
  root: { width: '100%', ...shorthands.padding(tokens.spacingVerticalS, 0) },
  list: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    '@media (max-width: 768px)': { overflowX: 'auto', justifyContent: 'flex-start', gap: tokens.spacingHorizontalL, paddingBottom: tokens.spacingVerticalS },
  },
  item: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  connector: {
    position: 'absolute',
    left: 'calc(50% + 20px)',
    right: 'calc(-50% + 20px)',
    top: '20px',
    height: '2px',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  connectorActive: { backgroundColor: tokens.colorBrandBackground },
  button: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  height: '40px',
  width: '40px',
    borderRadius: '9999px',
  ...shorthands.border('2px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    '@media (max-width: 768px)': { height: '32px', width: '32px', flexShrink: 0 },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
  },
  btnCompleted: {
  ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  btnCurrent: {
  ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    boxShadow: `0 0 0 4px ${tokens.colorBrandBackground2}`,
  },
  btnHoverable: {
    ':hover': { ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1), color: tokens.colorBrandForeground1, cursor: 'pointer' },
  },
  title: {
    textAlign: 'center',
    marginTop: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    minWidth: 0,
    '@media (max-width: 1200px)': { display: 'none' },
  },
  titleText: {
    fontSize: tokens.fontSizeBase300,
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    fontWeight: tokens.fontWeightSemibold,
  },
  titleMuted: { color: tokens.colorNeutralForeground4 },
  titleActive: { color: tokens.colorNeutralForeground1 },
  titleCompleted: { color: tokens.colorNeutralForeground2 },
  optional: { marginLeft: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  desc: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXXS,
  },
});

interface StepIndicatorProps {
  steps: StepItem[];
  currentStep: number;
  completedSteps?: number[];
  className?: string;
  onStepClick?: (stepIndex: number) => void;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps = [],
  className,
  onStepClick,
}) => {
  const styles = useStyles();
  const getStepStatus = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'current';
    if (stepIndex < currentStep) return 'completed';
    return 'upcoming';
  };

  return (
    <div className={`${styles.root} ${className ?? ''}`}>
      <nav aria-label="Progress">
        <ol className={styles.list}>
          {steps.map((step, stepIndex) => {
            const status = getStepStatus(stepIndex);
            const isClickable = onStepClick && stepIndex <= currentStep;

            return (
              <li key={step.id} className={styles.item}>
                {/* Step connector line */}
                {stepIndex !== steps.length - 1 && (
                  <div className={`${styles.connector} ${(status === 'completed' || stepIndex < currentStep) ? styles.connectorActive : ''}`} aria-hidden="true" />
                )}

                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Step indicator */}
                  <button
                    type="button"
                    onClick={isClickable ? () => onStepClick(stepIndex) : undefined}
                    disabled={!isClickable}
                    className={`${styles.button} ${status === 'completed' ? styles.btnCompleted : ''} ${status === 'current' ? styles.btnCurrent : ''} ${isClickable && status !== 'current' ? styles.btnHoverable : ''}`}
                    aria-current={status === 'current' ? 'step' : undefined}
                    aria-label={`Step ${stepIndex + 1}: ${step.title}${status === 'completed' ? ' (completed)' : status === 'current' ? ' (current)' : ''}`}
                  >
                    {status === 'completed' ? (
                      <Check aria-hidden="true" />
                    ) : (
                      <span style={{ fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold }}>{stepIndex + 1}</span>
                    )}
                  </button>

                  {/* Step title and description */}
                  <div className={styles.title}>
                    <div className={`${styles.titleText} ${status === 'current' ? styles.titleActive : status === 'completed' ? styles.titleCompleted : styles.titleMuted}`}>
                      {step.title}
                      {step.isOptional && (
                        <span className={styles.optional}>(Optional)</span>
                      )}
                    </div>
                    {step.description && (
                      <div className={styles.desc}>
                        {step.description}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

// Helper function to generate step configurations
export const getDomesticSteps = (): StepItem[] => [
  {
    id: 'type-of-request',
    title: 'Request Type',
    description: 'Basic request information',
  },
  {
    id: 'nature-of-crimes',
    title: 'Nature of Crimes',
    description: 'Crime type details',
  },
  {
    id: 'agent-information',
    title: 'Agent',
    description: 'Contact & delivery',
  },
  {
    id: 'request-parameters',
    title: 'Request Parameters',
    description: 'Date range & identifiers',
  },
  {
    id: 'legal-order-details',
    title: 'Legal Order Details',
    description: 'Request type',
  },
  {
    id: 'file-upload',
    title: 'Upload Files',
    description: 'Supporting documents',
  },
  {
    id: 'review-submit',
    title: 'Review & Submit',
    description: 'Final review',
  },
];

export const getInternationalSteps = (isDSACountry: boolean = false): StepItem[] => {
  const baseSteps: StepItem[] = [
    {
      id: 'type-of-request',
      title: 'Request Type',
      description: 'Basic information',
    },
    {
      id: 'nature-of-crimes',
      title: 'Nature of Crimes',
      description: 'Crime type details',
    },
    {
      id: 'agent-info',
      title: 'Agent',
      description: 'Contact details',
    },
    {
      id: 'type-of-service',
      title: 'Request Parameters',
      description: 'Date range & identifiers',
    },
    {
      id: 'legal-order-details',
      title: 'Legal Order Details',
      description: 'Request type',
    },
    {
      id: 'file-upload',
      title: 'Upload Files',
      description: 'Supporting documents',
    },
    {
      id: 'notification',
      title: 'Notification',
      description: 'Notice requirements',
    },
    {
      id: 'review-submit',
      title: 'Review & Submit',
      description: 'Final review',
    },
  ];

  if (isDSACountry) {
    baseSteps.splice(1, 0, {
      id: 'dsa-items',
      title: 'DSA Items',
      description: 'EU DSA compliance',
    });
  }

  return baseSteps;
};
